import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chatbotService } from '../services/chatbotService';
import { toast } from 'react-toastify';
import ChatbotConfigHeader from '../components/chatbot/layout/ChatbotConfigHeader';
import { Tabs } from '../components/ui/Tabs';
import GeneralTab from '../components/chatbot/tabs/GeneralTab';
import ThemesTab from '../components/chatbot/tabs/ThemesTab';
import PlaygroundTab from '../components/chatbot/tabs/PlaygroundTab';
import ConnectTab from '../components/chatbot/tabs/ConnectTab';
import ConversationsTab from '../components/chatbot/tabs/ConversationsTab';
import ConfigurationsTab from '../components/chatbot/tabs/ConfigurationsTab';
import { TabId, TabDef } from '../components/chatbot/types/chatbot.types';
import { Container, Section } from '../components/layout/primitives';
import {
  Cog6ToothIcon,
  SwatchIcon,
  BeakerIcon,
  LinkIcon,
  ChatBubbleLeftRightIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

/**
 * The chatbot configuration screen. Rendered as a page (/chatbots/:id/config) and inside
 * the pod workflow editor's "Configure chat" modal (`embedded`), which drops the page header.
 */
export const ChatbotConfigView: React.FC<{ chatbotId?: string; embedded?: boolean }> = ({ chatbotId, embedded }) => {
  const navigate = useNavigate();

  const [active, setActive] = useState<TabId>('general');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatbot, setChatbot] = useState<any>(null);

  useEffect(() => {
    if (!chatbotId) return;

    let mounted = true;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const resp = await chatbotService.getChatbotById(encodeURIComponent(chatbotId));
        const payload = (resp && resp.data) ? resp.data : resp;
        if (mounted) setChatbot(payload);
      } catch (err: any) {
        console.error('Failed to fetch chatbot', err);
        if (mounted) {
          setError(err?.message ?? 'Failed to load chatbot');
          toast.error('Failed to load chatbot');
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [chatbotId]);

  const tabs: TabDef[] = [
    { id: 'general', label: 'General', icon: Cog6ToothIcon, component: GeneralTab },
    { id: 'themes', label: 'Themes', icon: SwatchIcon, component: ThemesTab },
    { id: 'playground', label: 'Playground', icon: BeakerIcon, component: PlaygroundTab },
    { id: 'connect', label: 'Connect', icon: LinkIcon, component: ConnectTab },
    { id: 'conversations', label: 'Conversations', icon: ChatBubbleLeftRightIcon, component: ConversationsTab },
    { id: 'configurations', label: 'Configurations', icon: AdjustmentsHorizontalIcon, component: ConfigurationsTab },
  ];

  const ActiveComponent = tabs.find((t) => t.id === active)?.component;

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-agentic-text-secondary">Loading chatbot configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  const pod = chatbot?.podSubscription as
    | { aiPodId: string; podName: string; channel: string; workflowId: string | null; status: string }
    | null
    | undefined;
  const podPaused = !!pod && pod.status !== 'active' && pod.status !== 'trialing';

  const body = (
    <div className="flex flex-col gap-6 md:flex-row md:items-start">
      <Tabs variant="side-accent" items={tabs} value={active} onChange={(id) => setActive(id as TabId)} />
      <section className="min-w-0 flex-1">{ActiveComponent && chatbot && <ActiveComponent chatbot={chatbot} />}</section>
    </div>
  );

  if (embedded) return body;

  return (
    <div>
      <ChatbotConfigHeader
        chatbot={chatbot}
        onBack={() => navigate(pod ? `/pods/${pod.aiPodId}/manage` : '/chatbots')}
      />

      <Section className="py-8">
        <Container size="content">
          {pod && (
            <div
              className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
                podPaused ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-blue-200 bg-blue-50 text-blue-800'
              }`}
            >
              {podPaused
                ? `This chatbot is paused because your ${pod.podName} subscription isn't active.`
                : `This chatbot is part of your ${pod.podName} subscription.`}
            </div>
          )}
          {body}
        </Container>
      </Section>
    </div>
  );
};

const ChatbotConfig: React.FC = () => {
  const { chatbotId } = useParams<{ chatbotId: string }>();
  return <ChatbotConfigView chatbotId={chatbotId} />;
};

export default ChatbotConfig;
